import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import React, { PropsWithChildren, ReactNode, useMemo } from 'react'
import styled from 'styled-components'
import AccommodationRoomDetailsList from './AccommodationRoomDetailsList'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import { isHotelPackage } from 'lib/offer/offerTypes'

export const StyledAccommodationRoomTileDetails = styled(Group)`
  padding: ${rem(20)};
`

interface RoomPackageDetails {
  name: string
  description?: string
}

function extractHotelRoomPackageDetails(roomPackage: App.HotelPackage): RoomPackageDetails {
  return {
    name: roomPackage.roomType.group?.name ?? roomPackage.roomType.name,
    description: roomPackage.roomType.description,
  }
}

// Match a line that starts with two asterisks, as that implies we're dealing with Markdown. For
// example: "\n**Size:** 165m²\n\n**Sleeps:** 4 guests\n\n**Partial sea view**\n".
const BEDBANK_RICH_DESC_REGEX = /^\*\*/m

function extractBedbankRoomPackageDetails(roomPackage: App.BedbankPackage): RoomPackageDetails {
  return {
    name: roomPackage.name,
    description: !BEDBANK_RICH_DESC_REGEX.test(roomPackage.description) ? roomPackage.description : undefined,
  }
}

interface Props {
  roomPackage: App.HotelPackage | App.BedbankPackage
  urgencyLabels?: ReactNode
  actions?: ReactNode
}

function AccommodationRoomTileDetails(props: PropsWithChildren<Props>) {
  const {
    actions,
    children,
    roomPackage,
    urgencyLabels,
  } = props

  const details = useMemo(() => {
    if (isHotelPackage(roomPackage)) {
      return extractHotelRoomPackageDetails(roomPackage)
    } else { // is App.BedbankPackage
      return extractBedbankRoomPackageDetails(roomPackage)
    }
  }, [roomPackage])

  return <StyledAccommodationRoomTileDetails direction="vertical" gap={12}>
    <VerticalSpacer gap={4}>
      <Group direction="horizontal" horizontalAlign="space-between" gap={8}>
        <Heading variant="heading4">{details.name}</Heading>
        {actions && <Group direction="horizontal" gap={4} horizontalAlign="end" wrap="wrap" noDisplayWhenEmpty>
          {actions}
        </Group>}
      </Group>
      {urgencyLabels && <LabelGroup>{urgencyLabels}</LabelGroup>}
    </VerticalSpacer>
    {details.description && <BodyText variant="medium">{details.description}</BodyText>}
    <AccommodationRoomDetailsList roomPackage={roomPackage} />
    {children}
  </StyledAccommodationRoomTileDetails>
}

export default AccommodationRoomTileDetails
